import { JobProgressConsole } from '@/components/JobProgressConsole';
import { ResumenRegeneracion } from '@/components/sysadmin/ResumenRegeneracion';
import { Button, Modal } from '@/components/ui';
import { useBackgroundJob } from '@/hooks/useBackgroundJob';
import { sysadminService, type EstadoRegeneracionDto, type ResumenEjecucionDto } from '@/services/sysadmin.service';
import { AlertTriangle, Clock, Loader2, RefreshCw } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { AplicarHorasActasPanel } from './AplicarHorasActasPanel';

// Cada cuánto se le vuelve a preguntar al backend si la corrida sigue viva. El detalle del avance
// llega por SSE; esto es la red de seguridad para el caso en que el stream se corte o la página se
// haya cargado justo cuando la corrida terminaba.
const POLL_ESTADO_MS = 10_000;

export function RegenerarActasPage() {
  const [tab, setTab] = useState<'horas' | 'plantilla'>('horas');
  const [empresaIdsInput, setEmpresaIdsInput] = useState('');
  const [limitInput, setLimitInput] = useState('');
  const [force, setForce] = useState(false);
  const [pendientes, setPendientes] = useState<number | null>(null);
  const [loadingPendientes, setLoadingPendientes] = useState(true);
  const [errorPendientes, setErrorPendientes] = useState<string | null>(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [estadoServidor, setEstadoServidor] = useState<EstadoRegeneracionDto | null>(null);
  const [ultimaEjecucion, setUltimaEjecucion] = useState<ResumenEjecucionDto | null>(null);
  const [cargandoEstado, setCargandoEstado] = useState(true);

  const { isProcessing, isLoadingMore, hasMore, progress, summary, logs, startJob, connectOnly, loadMoreLogs } = useBackgroundJob(
    '/api/sysadmin/actas/regenerar',
    '/api/system-jobs/stream',
    { service: 'acta-regeneracion' }
  );

  const empresaIds = empresaIdsInput
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);

  const cargarPendientes = () => {
    setLoadingPendientes(true);
    setErrorPendientes(null);
    sysadminService
      .obtenerActasPendientesRegeneracion(empresaIds.length ? { empresaIds } : undefined)
      .then((res) => setPendientes(res.total))
      .catch((err) => setErrorPendientes(err instanceof Error ? err.message : 'Error al consultar actas pendientes'))
      .finally(() => setLoadingPendientes(false));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(cargarPendientes, [empresaIdsInput]);

  // Al entrar (y tras cada recarga) se le pregunta al backend si hay una corrida viva. Si la hay,
  // el botón queda bloqueado y nos reenganchamos al stream para ver el avance en curso.
  const yaReconectado = useRef(false);
  const sincronizarEstado = useCallback(async () => {
    try {
      const { estado, ultimaEjecucion: ultima } = await sysadminService.obtenerEstadoRegeneracion();
      setEstadoServidor(estado);
      if (ultima) setUltimaEjecucion(ultima);
      if (estado?.enCurso && !yaReconectado.current) {
        yaReconectado.current = true;
        connectOnly();
      }
      if (!estado?.enCurso) yaReconectado.current = false;
    } catch {
      // Si falla la consulta de estado no se toca el botón: mejor dejarlo como está que
      // habilitarlo por un error de red y arriesgar una segunda corrida.
    } finally {
      setCargandoEstado(false);
    }
  }, [connectOnly]);

  useEffect(() => {
    void sincronizarEstado();
    const id = setInterval(() => void sincronizarEstado(), POLL_ESTADO_MS);
    return () => clearInterval(id);
  }, [sincronizarEstado]);

  const handleConfirmarRegenerar = async () => {
    setShowConfirm(false);
    const limit = limitInput.trim() ? Number(limitInput.trim()) : undefined;
    await startJob({
      ...(empresaIds.length ? { empresaIds } : {}),
      ...(limit ? { limit } : {}),
      force,
    });
    // Refleja de inmediato la corrida recién arrancada (o el 409 si otro admin se adelantó).
    void sincronizarEstado();
  };

  const total = pendientes ?? 0;
  const enCurso = estadoServidor?.enCurso === true;
  // Mientras hay una corrida activa no se muestra el resumen anterior: confundiría con el avance.
  const resumenVisible = enCurso ? null : ((summary as ResumenEjecucionDto | null) ?? ultimaEjecucion);
  const botonBloqueado = enCurso || isProcessing || cargandoEstado || loadingPendientes || total === 0;

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-1.5">
        <h1 className="text-xl font-bold text-gray-900">Gestión y Migración de Actas</h1>
        <p className="text-sm text-gray-500">
          Herramientas de sysadmin para actualizar y regenerar actas: aplicar la fila AUDIOS (horas y nombres de archivo) a documentos ya
          transformados, o regenerar la plantilla completa reusando datos existentes.
        </p>
      </div>

      <div className="flex items-center gap-6 border-b border-gray-200">
        <button
          onClick={() => setTab('horas')}
          className={`pb-3 text-sm font-semibold flex items-center gap-2 border-b-2 whitespace-nowrap transition-colors ${
            tab === 'horas' ? 'border-blue-600 text-blue-700' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
          }`}
        >
          <Clock className="w-4.5 h-4.5" />
          Fila AUDIOS / Horas (Actas ya transformadas)
        </button>

        <button
          onClick={() => setTab('plantilla')}
          className={`pb-3 text-sm font-semibold flex items-center gap-2 border-b-2 whitespace-nowrap transition-colors ${
            tab === 'plantilla'
              ? 'border-blue-600 text-blue-700'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
          }`}
        >
          <RefreshCw className="w-4.5 h-4.5" />
          Regeneración Completa de Plantilla
        </button>
      </div>

      {tab === 'horas' ? (
        <AplicarHorasActasPanel />
      ) : (
        <>
          {enCurso && estadoServidor && (
            <div className="flex items-start gap-3 rounded-xl border border-blue-200 bg-blue-50 px-4 py-3.5">
              <Loader2 className="mt-0.5 h-5 w-5 shrink-0 animate-spin text-blue-600" />
              <div className="text-sm text-blue-900">
                <p className="font-semibold">Hay una regeneración en curso</p>
                <p className="mt-0.5 text-blue-800">
                  Iniciada a las {new Date(estadoServidor.iniciadoEn).toLocaleTimeString('es-CO')} ·{' '}
                  <span className="tabular-nums">
                    {estadoServidor.procesadas} de {estadoServidor.total}
                  </span>{' '}
                  actas procesadas
                  {estadoServidor.fallidas > 0 && <span className="tabular-nums"> · {estadoServidor.fallidas} con error</span>}
                  {estadoServidor.asesoriaActual !== null && <span> · procesando la asesoría {estadoServidor.asesoriaActual}</span>}
                </p>
                <p className="mt-1 text-xs text-blue-700">
                  El proceso corre en el servidor: puedes cerrar esta pantalla sin interrumpirlo. El botón queda bloqueado hasta que
                  termine, incluso si recargas la página.
                </p>
              </div>
            </div>
          )}

          <div className="p-6 bg-white rounded-xl shadow-sm border border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-bold text-gray-400 uppercase tracking-wider px-1">NITs de empresa (opcional)</label>
                <input
                  type="text"
                  value={empresaIdsInput}
                  onChange={(e) => setEmpresaIdsInput(e.target.value)}
                  disabled={isProcessing || enCurso}
                  placeholder="Ej: 80459115,900123456"
                  className="w-full bg-white border border-gray-200 rounded-lg py-2 px-3 text-sm text-gray-700 focus:ring-2 focus:ring-blue-500 outline-none transition-all disabled:opacity-50"
                />
                <span className="text-[11px] text-gray-400 px-1">Vacío = todas las empresas</span>
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-bold text-gray-400 uppercase tracking-wider px-1">Límite (opcional)</label>
                <input
                  type="number"
                  value={limitInput}
                  onChange={(e) => setLimitInput(e.target.value)}
                  disabled={isProcessing || enCurso}
                  placeholder="Ej: 50"
                  className="w-full bg-white border border-gray-200 rounded-lg py-2 px-3 text-sm text-gray-700 focus:ring-2 focus:ring-blue-500 outline-none transition-all disabled:opacity-50"
                />
              </div>
            </div>

            <label className="flex items-start gap-2 mb-6 text-sm text-gray-600 cursor-pointer">
              <input
                type="checkbox"
                checked={force}
                onChange={(e) => setForce(e.target.checked)}
                disabled={isProcessing || enCurso}
                className="mt-0.5"
              />
              <span>
                Reprocesar ya regeneradas
                <span className="block text-xs text-gray-400">
                  Por defecto, un acta cuyo documento actual ya sigue el formato nuevo se salta al procesarla (aunque el documento se haya
                  borrado a mano en Drive). Marcá esto para volver a regenerarla — se le crea otro documento nuevo, y el que tenga hoy en
                  actaLink pasa a "(versión anterior)".
                </span>
              </span>
            </label>

            {errorPendientes && (
              <div className="mb-4 flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">
                <AlertTriangle className="w-4 h-4 shrink-0" />
                {errorPendientes}
              </div>
            )}

            <div className="flex items-center justify-between gap-4">
              <span className="text-sm text-gray-500">
                {loadingPendientes
                  ? 'Consultando actas...'
                  : `${total} acta(s) cumplen estos filtros (las que ya se regeneraron se saltan solas).`}
              </span>
              <button
                onClick={() => setShowConfirm(true)}
                disabled={botonBloqueado}
                title={enCurso ? 'Ya hay una regeneración en curso' : undefined}
                className="shrink-0 inline-flex items-center gap-2 px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg shadow-md hover:shadow-lg transition-all text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <RefreshCw className={`w-4 h-4 ${enCurso || isProcessing ? 'animate-spin' : ''}`} />
                {enCurso || isProcessing ? 'Regeneración en curso...' : 'Regenerar actas'}
              </button>
            </div>
          </div>

          <div className="p-6 bg-white rounded-xl shadow-sm border border-gray-200">
            <JobProgressConsole
              progress={progress}
              logs={logs}
              hasMore={hasMore}
              isLoadingMore={isLoadingMore}
              onLoadMore={loadMoreLogs}
              isProcessing={isProcessing}
              // El resumen recién llegado por SSE gana; si no hay (p. ej. se recargó la página después
              // de que terminó), se usa el que guardó el backend de la última corrida.
              resumen={resumenVisible && <ResumenRegeneracion resumen={resumenVisible} />}
            />
          </div>

          <Modal
            isOpen={showConfirm}
            onClose={() => setShowConfirm(false)}
            size="md"
            title={
              <span className="flex items-center gap-3">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-amber-100">
                  <AlertTriangle className="h-4.5 w-4.5 text-amber-700" />
                </span>
                Confirmar regeneración de actas
              </span>
            }
          >
            <div className="space-y-5">
              <div className="flex items-baseline gap-2">
                <span className="text-3xl font-bold tabular-nums text-gray-900">{total}</span>
                <span className="text-sm text-gray-500">{total === 1 ? 'acta cumple los filtros' : 'actas cumplen los filtros'}</span>
              </div>
              <p className="-mt-3 text-xs text-gray-400">Las que ya se regeneraron antes se saltan solas al procesarlas.</p>

              <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 border-t border-gray-100 pt-4 text-sm">
                <dt className="text-gray-500">Empresas</dt>
                <dd className="text-right text-gray-900">{empresaIds.length ? empresaIds.join(', ') : 'Todas'}</dd>
                {limitInput.trim() && (
                  <>
                    <dt className="text-gray-500">Límite</dt>
                    <dd className="text-right text-gray-900">{limitInput.trim()}</dd>
                  </>
                )}
                <dt className="text-gray-500">Nombre del documento</dt>
                <dd className="text-right font-mono text-xs text-gray-900">AAAAMMDD_NOMBRE ACTIVIDAD</dd>
              </dl>

              <p className="text-sm leading-relaxed text-gray-600">
                Reusa el texto existente de "Acciones Adelantadas", salvo que tenga el bug conocido — ahí se regenera con IA. El documento
                viejo no se borra: queda en la misma carpeta, renombrado como "(versión anterior)". Puede tardar varias horas si son muchas
                actas.
              </p>

              {force && (
                <div className="flex items-start gap-2 rounded-lg bg-amber-50 px-3 py-2.5 text-sm text-amber-800">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
                  <span>Reprocesar está activo: incluye actas ya regeneradas antes — se les crea otro documento nuevo.</span>
                </div>
              )}

              <div className="flex justify-end gap-3 pt-1">
                <Button variant="secondary" onClick={() => setShowConfirm(false)}>
                  Cancelar
                </Button>
                <Button variant="warning" onClick={handleConfirmarRegenerar}>
                  Regenerar {total === 1 ? '1 acta' : `${total} actas`}
                </Button>
              </div>
            </div>
          </Modal>
        </>
      )}
    </div>
  );
}
